/**
 * Queries + mutations do CRUD de planos em `/superadmin/billing/plans`.
 * Consome as 3 RPCs criadas na migration 0055
 * (`admin_upsert_plan`, `admin_archive_plan`, `admin_unarchive_plan`).
 *
 * A query `usePlansAdmin` inclui arquivados (`is_active=false`) — necessário
 * pra o superadmin ver e restaurar planos que foram desativados. A policy
 * `plans_superadmin_select` (0055) permite esse SELECT sem filtro extra.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { supabase } from '../../lib/supabase.js';

export const PLANS_ADMIN_KEY = ['plans-admin'] as const;

/** Query que alimenta o dropdown de plano em `NewProposalDialog`.
 * Invalidamos depois de qualquer mutation pra o dropdown sempre
 * refletir o catálogo atual. */
export const PLANS_PUBLIC_KEY = ['plans', 'public'] as const;

export interface PlanRow {
  id: string;
  key: string;
  name_pt_br: string;
  description: string | null;
  tier: 'free' | 'pro' | 'enterprise' | 'custom';
  monthly_price_brl: number | null;
  yearly_price_brl: number | null;
  trial_days: number;
  max_users: number | null;
  max_patients: number | null;
  max_storage_gb: number | null;
  max_ai_tokens_monthly: number | null;
  features: string[];
  is_public: boolean;
  is_active: boolean;
  sort_order: number;
  billing_cycle: 'monthly' | 'yearly';
  asaas_default_billing_type: 'UNDEFINED' | 'BOLETO' | 'CREDIT_CARD' | 'PIX';
  created_at: string;
  updated_at: string;
}

/** Shape reduzido dos planos pra consumers não-admin (form de proposta,
 * etc). Omite arquivados e apenas-internos (is_public=false). Versão leve
 * de `PlanRow` — só campos usados em selects/defaults. */
export interface PlanForPicker {
  id: string;
  key: string;
  name_pt_br: string;
  description: string | null;
  monthly_price_brl: number | null;
  yearly_price_brl: number | null;
  trial_days: number;
  billing_cycle: 'monthly' | 'yearly';
  sort_order: number;
}

/** Lista de planos ativos + públicos, pra alimentar dropdowns comerciais
 * (criar proposta, exibir catálogo). Segue policy `plans_superadmin_select`
 * da migration 0055; authenticated não-superadmin vê apenas o que
 * `plans_public_read` expõe (is_active=true + is_public=true), então o
 * mesmo hook serve os 2 casos — o filtro é redundante mas explícito
 * pra evitar vazamento acidental de plano interno/arquivado em UI.
 *
 * Usado em `NewProposalDialog` / `ProposalSaasFields` pra substituir o
 * enum hardcoded ['free','pro','enterprise']. PR fix/proposal-plan-dropdown. */
export function usePlansForPicker() {
  return useQuery<PlanForPicker[], Error>({
    queryKey: PLANS_PUBLIC_KEY,
    staleTime: 5 * 60_000, // 5min — catálogo muda raramente
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plans')
        .select(
          'id, key, name_pt_br, description, monthly_price_brl, yearly_price_brl, trial_days, billing_cycle, sort_order',
        )
        .eq('is_active', true)
        .eq('is_public', true)
        .order('sort_order', { ascending: true })
        .order('name_pt_br', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as PlanForPicker[];
    },
  });
}

export function usePlansAdmin() {
  return useQuery<PlanRow[], Error>({
    queryKey: PLANS_ADMIN_KEY,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plans')
        .select(
          'id, key, name_pt_br, description, tier, monthly_price_brl, yearly_price_brl, trial_days, max_users, max_patients, max_storage_gb, max_ai_tokens_monthly, features, is_public, is_active, sort_order, billing_cycle, asaas_default_billing_type, created_at, updated_at',
        )
        .order('sort_order', { ascending: true })
        .order('name_pt_br', { ascending: true });
      if (error) throw new Error(error.message);
      // `features` vem como jsonb; normaliza pra string[]. Cast via
      // `unknown` porque o shape vindo do supabase-js é opaco
      // (Json branded type) e o schema dessa tabela ainda não foi
      // regenerado no `@kmgestao/db-types`.
      return ((data ?? []) as unknown as PlanRow[]).map((row) => ({
        ...row,
        features: Array.isArray(row.features) ? row.features : [],
      }));
    },
  });
}

export function useUpsertPlan(): UseMutationResult<string, Error, Record<string, unknown>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data, error } = await supabase.rpc(
        'admin_upsert_plan' as never,
        { p: payload } as never,
      );
      if (error) throw new Error(error.message);
      return data as unknown as string;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLANS_ADMIN_KEY });
      void qc.invalidateQueries({ queryKey: PLANS_PUBLIC_KEY });
    },
  });
}

export function useArchivePlan(): UseMutationResult<void, Error, { key: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key }) => {
      const { error } = await supabase.rpc('admin_archive_plan' as never, { p_key: key } as never);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLANS_ADMIN_KEY });
      void qc.invalidateQueries({ queryKey: PLANS_PUBLIC_KEY });
    },
  });
}

export function useUnarchivePlan(): UseMutationResult<void, Error, { key: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key }) => {
      const { error } = await supabase.rpc(
        'admin_unarchive_plan' as never,
        { p_key: key } as never,
      );
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLANS_ADMIN_KEY });
      void qc.invalidateQueries({ queryKey: PLANS_PUBLIC_KEY });
    },
  });
}
